/**
 * iter-386u-ax — CommissionPreviewChip.
 * Shows the 5% platform fee breakdown live while the user types a vendor
 * payment amount. Only renders when the backend confirms the fee is active
 * (Enterprise sub-tenant + charge_own_vendors=true). Silently invisible for
 * every other user so the UX stays clean for normal Bella Casa operators.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { IndianRupee } from 'lucide-react';

const money = (paise) => `\u20B9${((Number(paise) || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CommissionPreviewChip({ amountRupees }) {
  const [preview, setPreview] = useState(null);
  const paise = Math.round((Number(amountRupees) || 0) * 100);

  useEffect(() => {
    let cancelled = false;
    if (!paise || paise <= 0) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/admin/payouts/vendor/preview`,
          { amount_paise: paise },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (!cancelled) setPreview(r.data);
      } catch { if (!cancelled) setPreview(null); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [paise]);

  if (!preview || !preview.fee_active) return null;

  return (
    <div className="mt-2 p-3 rounded-lg border border-amber-300 bg-amber-50 text-xs" data-testid="commission-preview-chip">
      <div className="flex items-center gap-2 font-semibold text-amber-900">
        <IndianRupee size={12} />
        Platform fee preview · {preview.commission_percentage}%
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-stone-700">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500">Gross</div>
          <div className="font-medium">{money(preview.gross_paise)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500">Fee</div>
          <div className="font-medium text-amber-800">− {money(preview.commission_paise)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500">Vendor gets</div>
          <div className="font-semibold text-emerald-700">{money(preview.net_paise)}</div>
        </div>
      </div>
      <div className="mt-2 text-[10px] text-stone-500 leading-relaxed">
        {preview.reason}. Configurable via <code>organization_settings.platform_transaction_fee_percentage</code>.
      </div>
    </div>
  );
}
